import { MatchState } from '@/types';
import { getMatchWithDetails, getPlayerOfMatch, getTeams, getTimeoutsForMatch } from '@/lib/supabase';

// ─── Export Match by ID (for use from Tournament Dashboard) ───
export async function exportMatchById(matchId: string) {
  const [matchData, teamsData] = await Promise.all([
    getMatchWithDetails(matchId),
    getTeams(),
  ]);

  if (!matchData) throw new Error('Match not found');

  const teamA = teamsData.find((t: any) => t.id === matchData.team_a_id) as any;
  const teamB = teamsData.find((t: any) => t.id === matchData.team_b_id) as any;

  if (!teamA || !teamB) throw new Error('Teams not found');

  // Build a minimal matchState-like object for export
  const sets = (matchData.sets || []).map((s: any) => ({
    id: s.id,
    matchId: matchData.id,
    setNumber: s.set_number,
    scoreA: s.score_a,
    scoreB: s.score_b,
    winnerId: s.winner_id,
    startedAt: s.started_at,
    endedAt: s.ended_at,
    rallies: (s.rallies || []).map((r: any) => ({
      id: r.id,
      setId: s.id,
      rallyNumber: r.rally_number,
      servingTeamId: r.serving_team_id,
      serverId: r.server_id,
      pointWonById: r.point_won_by_id,
      lostReason: r.lost_reason,
      pointWinnerId: r.point_winner_id,
      recordedAt: r.recorded_at,
    })),
  }));

  const teamASetsWon = sets.filter((s: any) => s.winnerId === teamA.id).length;
  const teamBSetsWon = sets.filter((s: any) => s.winnerId === teamB.id).length;

  const matchStateLike = {
    match: {
      id: matchData.id,
      matchNumber: matchData.match_number,
      date: matchData.date,
      matchTime: matchData.match_time,
      matchType: matchData.match_type,
      teamAId: matchData.team_a_id,
      teamBId: matchData.team_b_id,
      tossWonById: matchData.toss_won_by_id,
      tossChoice: matchData.toss_choice,
      status: matchData.status,
      winnerId: matchData.winner_id,
      bestCatcherIds: matchData.best_catcher_ids || [],
      tournamentId: matchData.tournament_id,
      winningPoint: matchData.winning_point,
      goldenPoint: matchData.golden_point,
      createdAt: matchData.created_at,
      updatedAt: matchData.updated_at,
    },
    teamA: {
      id: teamA.id,
      name: teamA.name,
      shortName: teamA.short_name ?? teamA.shortName,
      color: teamA.color,
      players: teamA.players || [],
      setsWon: teamASetsWon,
      rotationState: { serveHistory: [], cycleServed: [] },
    },
    teamB: {
      id: teamB.id,
      name: teamB.name,
      shortName: teamB.short_name ?? teamB.shortName,
      color: teamB.color,
      players: teamB.players || [],
      setsWon: teamBSetsWon,
      rotationState: { serveHistory: [], cycleServed: [] },
    },
    sets,
    bestCatcherIds: matchData.best_catcher_ids || [],
    currentSet: null,
    currentSetNumber: sets.length,
    status: matchData.status,
  };

  await exportMatchReport(matchStateLike as any, []);
}

// ─── Match Report Export ───
export async function exportMatchReport(matchState: MatchState, _teams: any[]) {
  const XLSX = await import('xlsx');
  const match = matchState.match!;
  const teamA = matchState.teamA!;
  const teamB = matchState.teamB!;

  const matchWinner = teamA.setsWon > teamB.setsWon ? teamA.name : teamB.name;
  void matchWinner;
  const tossWonBy = teamA.id === match.tossWonById ? teamA.shortName : teamB.shortName;
  const bestCatcherNames = matchState.bestCatcherIds
    .map(id => {
      const player = teamA.players.find(p => p.id === id) || teamB.players.find(p => p.id === id);
      return player ? player.name : id;
    });

  // Fetch Player of the Match
  let playerOfMatchNames: string[] = [];
  if (match.id) {
    try {
      const winners = await getPlayerOfMatch(match.id);
      console.log('[ExportMatchReport] getPlayerOfMatch returned', winners.length, 'winner(s):', winners.map(w => `${w.playerName} (${w.teamName}) ${w.totalStars} stars`));
      playerOfMatchNames = winners.map(w => `${w.playerName} (${w.teamName})`);
    } catch (err) {
      console.error('[ExportMatchReport] getPlayerOfMatch failed:', err);
    }
  } else {
    console.log('[ExportMatchReport] No match.id, skipping getPlayerOfMatch');
  }

  // Sheet 1: Match Summary
  const summaryData = [
    ['Field', 'Value'],
    ['Match Number', match.matchNumber],
    ['Match Date', match.date],
    ['Team A', `${teamA.shortName} (${teamA.name})`],
    ['Team B', `${teamB.shortName} (${teamB.name})`],
    ['Match Type', match.matchType],
    ['Toss Won By', tossWonBy],
    ['Choose To', match.tossChoice],
    ['Match Winner', teamA.setsWon > teamB.setsWon ? teamA.shortName : teamB.shortName],
    ['Player of the Match', playerOfMatchNames.length > 0 ? playerOfMatchNames.join(', ') : 'Not determined'],
    ['Best Catcher', bestCatcherNames.join(', ')],
    ['Match Locked', matchState.match?.isLocked ? 'Yes' : 'No'],
  ];

  // Set scores
  const setScores = matchState.sets.map(set => {
    const winner = teamA.id === set.winnerId ? teamA.shortName : teamB.shortName;
    return {
      'Set Number': set.setNumber,
      'Team A': teamA.shortName,
      'Score A': set.scoreA,
      'Team B': teamB.shortName,
      'Score B': set.scoreB,
      'Set Winner': winner,
      'Start Time': set.startedAt,
      'End Time': set.endedAt,
    };
  });

  const fullMatchData = await getMatchWithDetails(match.id);

  // Rally history
  const rallyHistory = fullMatchData.sets.flatMap((set: any) => {
    const allPlayers = [...teamA.players, ...teamB.players];
    return set.rallies.map((rally: any) => {
      const server = allPlayers.find(p => p.id === rally.server_id);
      const winningTeam = rally.point_won_by_id === teamA.id ? teamA : teamB;
      const pointWinner = rally.point_winner_id ? allPlayers.find(p => p.id === rally.point_winner_id) : null;
      return {
        'Set Number': set.set_number,
        'Rally Number': rally.rally_number,
        'Serving Team': rally.serving_team_id === teamA.id ? teamA.shortName : teamB.shortName,
        'Server': server ? server.name : rally.server_id,
        'Winning Team': winningTeam ? winningTeam.shortName : rally.point_won_by_id,
        'Lost Reason': rally.lost_reason,
        'Point Winner': pointWinner ? pointWinner.name : '',
      };
    });
  });

  // Team stats
  const teamAStats = fullMatchData.sets.map((set: any) => {
    const teamARallies = set.rallies.filter((r: any) => r.point_won_by_id === teamA.id);
    return {
      'Set Number': set.set_number,
      'Team A Score': teamARallies.length,
      'Team A Points': teamARallies.length,
      'Team A Score (Team A Won)': teamARallies.length,
      'Team A Score (Team B Won)': set.rallies.length - teamARallies.length,
    };
  });

  const teamBStats = fullMatchData.sets.map((set: any) => {
    const teamBRallies = set.rallies.filter((r: any) => r.point_won_by_id === teamB.id);
    return {
      'Set Number': set.set_number,
      'Team B Score': teamBRallies.length,
      'Team B Points': teamBRallies.length,
      'Team B Score (Team A Won)': set.rallies.length - teamBRallies.length,
      'Team B Score (Team B Won)': teamBRallies.length,
    };
  });

  const teamAPlayersStats = teamA.players.map((player: any) => {
    const playerStats = fullMatchData.sets.flatMap((set: any) => {
      const setStats = set.rallies.filter((r: any) => r.point_winner_id === player.id);
      return setStats.map((rally: any) => {
        const winningTeam = rally.point_won_by_id === teamA.id ? teamA : teamB;
        const lostReason = rally.lost_reason;
        const point = lostReason === 'ACE' ? winningTeam.name === teamA.name ? teamA.name : teamB.name : winningTeam.name === teamA.name ? teamA.name : teamB.name;
        const foul = lostReason === 'FOUL' ? 'Yes' : 'No';
        return {
          'Set Number': rally.set_number,
          'Rally Number': rally.rally_number,
          'Player': player.name,
          'Team': teamA.name,
          'Point': point,
          'Lost Reason': lostReason,
          'Foul': foul,
        };
      });
    });
    return playerStats;
  });

  const teamBPlayersStats = teamB.players.map((player: any) => {
    const playerStats = fullMatchData.sets.flatMap((set: any) => {
      const setStats = set.rallies.filter((r: any) => r.point_winner_id === player.id);
      return setStats.map((rally: any) => {
        const winningTeam = rally.point_won_by_id === teamA.id ? teamA : teamB;
        const lostReason = rally.lost_reason;
        const point = lostReason === 'ACE' ? winningTeam.name === teamA.name ? teamA.name : teamB.name : winningTeam.name === teamA.name ? teamA.name : teamB.name;
        const foul = lostReason === 'FOUL' ? 'Yes' : 'No';
        return {
          'Set Number': rally.set_number,
          'Rally Number': rally.rally_number,
          'Player': player.name,
          'Team': teamB.name,
          'Point': point,
          'Lost Reason': lostReason,
          'Foul': foul,
        };
      });
    });
    return playerStats;
  });

  const wb = XLSX.utils.book_new();

  const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
  XLSX.utils.book_append_sheet(wb, summarySheet, 'Match Summary');

  const setScoresSheet = XLSX.utils.json_to_sheet(setScores);
  XLSX.utils.book_append_sheet(wb, setScoresSheet, 'Set Scores');

  const rallyHistorySheet = XLSX.utils.json_to_sheet(rallyHistory);
  XLSX.utils.book_append_sheet(wb, rallyHistorySheet, 'Rally History');

  const teamAStatsSheet = XLSX.utils.json_to_sheet(teamAStats);
  XLSX.utils.book_append_sheet(wb, teamAStatsSheet, 'Team A Stats');

  const teamBStatsSheet = XLSX.utils.json_to_sheet(teamBStats);
  XLSX.utils.book_append_sheet(wb, teamBStatsSheet, 'Team B Stats');

  const teamAPlayersStatsSheet = XLSX.utils.json_to_sheet(teamAPlayersStats.flat());
  XLSX.utils.book_append_sheet(wb, teamAPlayersStatsSheet, 'Team A Players');

  const teamBPlayersStatsSheet = XLSX.utils.json_to_sheet(teamBPlayersStats.flat());
  XLSX.utils.book_append_sheet(wb, teamBPlayersStatsSheet, 'Team B Players');

  // Timeout data
  let timeoutData: { 'Set': number; 'Team': string; 'Start Time': string; 'End Time': string; 'Duration (seconds)': number }[] = [];
  if (match.id) {
    try {
      const timeouts = await getTimeoutsForMatch(match.id);
      timeoutData = timeouts.map(t => ({
        'Set': t.setNumber,
        'Team': t.teamName,
        'Start Time': t.startTimestamp,
        'End Time': t.endTimestamp || 'N/A',
        'Duration (seconds)': t.durationSeconds || 0,
      }));
    } catch (err) {
      console.warn('[ExportMatchReport] Could not load timeouts:', err);
    }
  }
  if (timeoutData.length > 0) {
    const timeoutsSheet = XLSX.utils.json_to_sheet(timeoutData);
    XLSX.utils.book_append_sheet(wb, timeoutsSheet, 'Timeouts');
  }

  XLSX.writeFile(wb, `match_${match.matchNumber}_report.xlsx`);
}

export async function exportSetData(matchState: MatchState, setNumber: number) {
  const XLSX = await import('xlsx');
  const match = matchState.match!;
  const teamA = matchState.teamA!;
  const teamB = matchState.teamB!;
  const set = matchState.sets[setNumber - 1];

  if (!set) {
    console.error('[exportSetData] Set not found:', setNumber);
    return;
  }

  const wb = XLSX.utils.book_new();

  const summaryData = [
    ['Field', 'Value'],
    ['Match Number', match.matchNumber],
    ['Set Number', setNumber],
    ['Team A', teamA.name],
    ['Team B', teamB.name],
    ['Set Winner', teamA.id === set.winnerId ? teamA.name : teamB.name],
  ];

  const rallyData = (set.rallies ?? []).map(r => {
    const allPlayers = [...teamA.players, ...teamB.players];
    const server = allPlayers.find(p => p.id === r.serverId);
    const winningTeam = r.pointWonById === teamA.id ? teamA : teamB;
    const pointWinner = r.pointWinnerId ? allPlayers.find(p => p.id === r.pointWinnerId) : null;

    return {
      'Rally': r.rallyNumber,
      'Serving Team': r.servingTeamId === teamA.id ? teamA.name : teamB.name,
      'Server': server?.name ?? '',
      'Winning Team': winningTeam.name,
      'Lost Reason': r.lostReason ?? '',
      'Point Winner': pointWinner?.name ?? '',
    };
  });

  const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
  XLSX.utils.book_append_sheet(wb, summarySheet, 'Set Summary');

  const rallySheet = XLSX.utils.json_to_sheet(rallyData);
  XLSX.utils.book_append_sheet(wb, rallySheet, 'Rallies');

  XLSX.writeFile(wb, `match_${match.matchNumber}_set_${setNumber}.xlsx`);
}

// ─── Tournament Export ───



export async function exportTournamentData(rawData: any, teams: any[], participatingTeamIds?: string[]) {
  const XLSX = await import('xlsx');
  // ── Defensive: normalise input ─────────────────────────────────
  const matches = Array.isArray(rawData) ? rawData : rawData?.matches ?? [];
  console.log('[ExportTournamentData] rawData type:', typeof rawData, 'Array.isArray:', Array.isArray(rawData));
  console.log('[ExportTournamentData] matches count:', matches?.length ?? 0);
  console.log('[ExportTournamentData] teams passed in:', teams?.length ?? 0);

  // Build tournamentTeams map from the passed teams array (UI state), fallback to match-level
  const allTeamPlayers: any[] = [];
  const tournamentTeams: any[] = (teams ?? []).map((t: any) => {
    const players = Array.isArray(t.players) ? t.players : [];
    players.forEach((p: any) => allTeamPlayers.push({ ...p, team_id: t.id }));
    return {
      id: t.id,
      name: t.name,
      short_name: t.shortName,
      color: t.color,
      players,
      logoUrl: t.logoUrl || t.logo_url,
    };
  });

  // Filter to participating teams only if specified
  const participatingTeams = participatingTeamIds && participatingTeamIds.length > 0
    ? tournamentTeams.filter(t => participatingTeamIds.includes(t.id))
    : tournamentTeams;

  // ── Helpers ──────────────────────────────────────────────────────
  const safeArr = (v: any) => (Array.isArray(v) ? v : []);
  const safeStr = (v: any) => (v == null ? '' : String(v));
  const safeNum = (v: any) => (typeof v === 'number' ? v : 0);
  const safePlayerName = (id: string | null | undefined, _teamId?: string | null) => {
    if (!id) return '';
    // Search in all team players first
    let p = allTeamPlayers.find((x: any) => x.id === id);
    if (p) return `${p.name}`;
    // Search in passed teams
    for (const t of teams ?? []) {
      const found = t.players?.find((x: any) => x.id === id);
      if (found) return found.name;
    }
    return '';
  };
  const safeTeamName = (id: string | null | undefined) => {
    const t = tournamentTeams.find((x: any) => x.id === id);
    return t?.name ?? '';
  };
  const safeTeamShortName = (id: string | null | undefined) => {
    const t = tournamentTeams.find((x: any) => x.id === id);
    return t?.short_name ?? '';
  };
  const safeTeamFromMatch = (match: any, id: string | null | undefined) => {
    const fromTeams = tournamentTeams.find((t: any) => t.id === id);
    if (fromTeams) return fromTeams;
    // Fallback: build from match data
    const teamData = id === match.team_a_id ? match.team_a : match.team_b;
    if (teamData && typeof teamData === 'object') return { ...teamData, short_name: teamData.shortName ?? teamData.short_name };
    return { name: '', short_name: '' };
  };

  // ── All Matches sheet ───────────────────────────────────────────
  const matchesData: (string | number)[][] = [
    ['Match #', 'Date', 'Type', 'Team A', 'Team B', 'Toss Won By', 'Choose To', 'Winner', 'Status', 'Locked', 'Player of the Match', 'Best Catcher'],
  ];

  for (const m of matches) {
    if (!m || typeof m !== 'object') {
      console.log('[ExportTournamentData] Skipping invalid match entry:', m);
      continue;
    }
    const teamA = safeTeamFromMatch(m, m.team_a_id);
    const teamB = safeTeamFromMatch(m, m.team_b_id);
    const tossWonShort = safeTeamShortName(m.toss_won_by_id);
    const winnerShort = safeTeamShortName(m.winner_id);

    // Determine Player of the Match
    let playerOfMatch: string[] = [];
    if (m.player_of_match_ids && Array.isArray(m.player_of_match_ids) && m.player_of_match_ids.length > 0) {
      const pomTeamId = m.player_of_match_team_id ?? m.winner_id;
      const pomTeam = tournamentTeams.find((t: any) => t.id === pomTeamId);
      playerOfMatch = m.player_of_match_ids
        .map((pid: string) => {
          const p = pomTeam?.players?.find((x: any) => x.id === pid);
          return p ? `${p.name} (${pomTeam?.short_name ?? ''})` : '';
        })
        .filter(Boolean);
      console.log('[ExportTournamentData] Match', m.match_number, 'POM from DB:', playerOfMatch);
    } else if (m.status === 'match-won' && m.winner_id) {
      const winnerTeam = tournamentTeams.find((t: any) => t.id === m.winner_id);
      if (winnerTeam) {
        const matchStars = new Map<string, number>();
        for (const s of safeArr(m.sets)) {
          for (const r of safeArr(s?.rallies)) {
            if (r?.lost_reason === 'ACE') matchStars.set(r.server_id, (matchStars.get(r.server_id) ?? 0) + 1);
            if (r?.point_winner_id) matchStars.set(r.point_winner_id, (matchStars.get(r.point_winner_id) ?? 0) + 1);
          }
        }
        if (Array.isArray(m.best_catcher_ids)) {
          for (const pid of m.best_catcher_ids) matchStars.set(pid, (matchStars.get(pid) ?? 0) + 1);
        }
        let max = 0;
        for (const p of winnerTeam.players ?? []) max = Math.max(max, matchStars.get(p.id) ?? 0);
        if (max > 0) {
          for (const p of winnerTeam.players ?? []) {
            if ((matchStars.get(p.id) ?? 0) === max) {
              playerOfMatch.push(`${p.name} (${winnerTeam.short_name ?? ''})`);
            }
          }
        }
        console.log('[ExportTournamentData] Match', m.match_number, 'POM computed dynamically:', playerOfMatch);
      }
    }

    const bestCatcherNames = (m.best_catcher_ids ?? [])
      .map((id: string) => safePlayerName(id))
      .filter(Boolean)
      .join(', ') || 'N/A';

    const lockedStatus = m.is_locked ? 'Yes' : 'No';

    matchesData.push([
      safeNum(m.match_number),
      safeStr(m.date),
      safeStr(m.match_type),
      safeStr(teamA?.short_name),
      safeStr(teamB?.short_name),
      safeStr(tossWonShort),
      safeStr(m.toss_choice),
      safeStr(winnerShort),
      safeStr(m.status),
      lockedStatus,
      playerOfMatch.length > 0 ? playerOfMatch.join(', ') : 'Not determined',
      bestCatcherNames,
    ]);
  }

  // ── All Set Scores sheet ─────────────────────────────────────────
  const setScoresData: (string | number)[][] = [
    ['Match #', 'Set #', 'Team A', 'Score A', 'Team B', 'Score B', 'Set Winner'],
  ];

  for (const m of matches) {
    if (!m || !m.sets) continue;
    const teamA = safeTeamFromMatch(m, m.team_a_id);
    const teamB = safeTeamFromMatch(m, m.team_b_id);
    for (const s of safeArr(m.sets)) {
      if (!s) continue;
      const winner = (s.score_a ?? 0) > (s.score_b ?? 0) ? teamA : teamB;
      setScoresData.push([
        safeNum(m.match_number),
        safeNum(s.set_number),
        safeStr(teamA?.short_name),
        safeNum(s.score_a),
        safeStr(teamB?.short_name),
        safeNum(s.score_b),
        safeStr(winner?.short_name),
      ]);
    }
  }

  // ── Player Of The Match sheet ──────────────────────────────────
  const pomRows: (string | number)[][] = [['Match #', 'Player', 'Team', 'Total Stars']];
  for (const m of matches) {
    if (m?.status !== 'match-won' || !m.winner_id) continue;
    const winnerTeam = tournamentTeams.find((t: any) => t.id === m.winner_id);
    if (!winnerTeam) continue;

    let playerOfMatch: { name: string; team: string; stars: number }[] = [];

    if (Array.isArray(m.player_of_match_ids) && m.player_of_match_ids.length > 0) {
      const pomTeamId = m.player_of_match_team_id ?? m.winner_id;
      const pomTeam = tournamentTeams.find((t: any) => t.id === pomTeamId);
      const matchStars = new Map<string, number>();
      for (const s of safeArr(m.sets)) {
        for (const r of safeArr(s?.rallies)) {
          if (r?.lost_reason === 'ACE') matchStars.set(r.server_id, (matchStars.get(r.server_id) ?? 0) + 1);
          if (r?.point_winner_id) matchStars.set(r.point_winner_id, (matchStars.get(r.point_winner_id) ?? 0) + 1);
        }
      }
      if (Array.isArray(m.best_catcher_ids)) {
        for (const pid of m.best_catcher_ids) matchStars.set(pid, (matchStars.get(pid) ?? 0) + 1);
      }
      for (const pid of m.player_of_match_ids) {
        const player = pomTeam?.players?.find((p: any) => p.id === pid);
        const stars = matchStars.get(pid) ?? 0;
        if (player) {
          playerOfMatch.push({ name: player.name, team: pomTeam?.short_name ?? '', stars });
        }
      }
    } else {
      const matchStars = new Map<string, number>();
      for (const s of safeArr(m.sets)) {
        for (const r of safeArr(s?.rallies)) {
          if (r?.lost_reason === 'ACE') matchStars.set(r.server_id, (matchStars.get(r.server_id) ?? 0) + 1);
          if (r?.point_winner_id) matchStars.set(r.point_winner_id, (matchStars.get(r.point_winner_id) ?? 0) + 1);
        }
      }
      if (Array.isArray(m.best_catcher_ids)) {
        for (const pid of m.best_catcher_ids) matchStars.set(pid, (matchStars.get(pid) ?? 0) + 1);
      }
      let max = 0;
      for (const p of winnerTeam.players ?? []) max = Math.max(max, matchStars.get(p.id) ?? 0);
      if (max === 0) continue;
      for (const p of winnerTeam.players ?? []) {
        const stars = matchStars.get(p.id) ?? 0;
        if (stars === max) playerOfMatch.push({ name: p.name, team: winnerTeam.short_name ?? '', stars });
      }
    }

    for (const p of playerOfMatch) {
      pomRows.push([safeNum(m.match_number), p.name, p.team, p.stars]);
    }
  }

  // ── Points Table (compute from match data) ───────────────────────
  const pointsTable: any[] = [];
  const teamStats = new Map<string, { played: number; wins: number; losses: number; points: number; setsWon: number; setsLost: number; pointsScored: number; pointsConceded: number }>();
  for (const m of matches) {
    if (!m || m.status !== 'match-won') continue;
    const a = teamStats.get(m.team_a_id) ?? { played: 0, wins: 0, losses: 0, points: 0, setsWon: 0, setsLost: 0, pointsScored: 0, pointsConceded: 0 };
    const b = teamStats.get(m.team_b_id) ?? { played: 0, wins: 0, losses: 0, points: 0, setsWon: 0, setsLost: 0, pointsScored: 0, pointsConceded: 0 };
    a.played++; b.played++;
    const aSetsWon = safeArr(m.sets).filter((s: any) => s?.winner_id === m.team_a_id).length;
    const bSetsWon = safeArr(m.sets).filter((s: any) => s?.winner_id === m.team_b_id).length;
    a.setsWon += aSetsWon; b.setsWon += bSetsWon;
    a.setsLost += bSetsWon; b.setsLost += aSetsWon;
    const aPoints = safeArr(m.sets).reduce((sum: number, s: any) => sum + (s?.score_a ?? 0), 0);
    const bPoints = safeArr(m.sets).reduce((sum: number, s: any) => sum + (s?.score_b ?? 0), 0);
    a.pointsScored += aPoints; a.pointsConceded += bPoints;
    b.pointsScored += bPoints; b.pointsConceded += aPoints;
    if (m.winner_id === m.team_a_id) { a.wins++; a.points += 2; b.losses++; }
    else { b.wins++; b.points += 2; a.losses++; }
    teamStats.set(m.team_a_id, a); teamStats.set(m.team_b_id, b);
  }
  const sortedTeams = [...teamStats.entries()].sort((a, b) => b[1].points - a[1].points || (b[1].setsWon - b[1].setsLost) - (a[1].setsWon - a[1].setsLost));
  sortedTeams.forEach((entry, idx) => {
    const [teamId, stats] = entry;
    const setWinPct = stats.played > 0 ? ((stats.setsWon / (stats.setsWon + stats.setsLost)) * 100).toFixed(1) : '0.0';
    pointsTable.push({
      position: idx + 1,
      teamName: safeTeamName(teamId),
      played: stats.played,
      wins: stats.wins,
      losses: stats.losses,
      points: stats.points,
      setsWon: stats.setsWon,
      setsLost: stats.setsLost,
      setWinPct,
      pointsScored: stats.pointsScored,
      pointsConceded: stats.pointsConceded,
      netPoints: stats.pointsScored - stats.pointsConceded,
    });
  });

  const pointsTableRows: (string | number)[][] = [
    ['Position', 'Team', 'Played', 'Won', 'Lost', 'Points', 'Sets Won', 'Sets Lost', 'Set Win %', 'Points Scored', 'Points Conceded', 'Net Points'],
    ...pointsTable.map((row: any) => [
      row.position,
      row.teamName,
      row.played,
      row.wins,
      row.losses,
      row.points,
      row.setsWon,
      row.setsLost,
      row.setWinPct,
      row.pointsScored,
      row.pointsConceded,
      row.netPoints,
    ]),
  ];

  // ── Player Stars (compute from match data) ─────────────────────
  const playerStarsMap = new Map<string, { playerName: string; teamShortName: string; throwerStars: number; acerStars: number; catcherStars: number; totalStars: number }>();
  for (const m of matches) {
    if (!m || !m.sets) continue;
    for (const s of safeArr(m.sets)) {
      for (const r of safeArr(s?.rallies)) {
        if (!r) continue;
        const teamId = r.serving_team_id || r.point_won_by_id;
        const team = tournamentTeams.find((t: any) => t.id === teamId);
        if (r.lost_reason === 'ACE' && r.server_id) {
          const p = allTeamPlayers.find((x: any) => x.id === r.server_id);
          const entry = playerStarsMap.get(r.server_id) ?? { playerName: p?.name ?? '', teamShortName: team?.short_name ?? '', throwerStars: 0, acerStars: 0, catcherStars: 0, totalStars: 0 };
          entry.acerStars += 1; entry.totalStars += 1;
          playerStarsMap.set(r.server_id, entry);
        }
        if (r.point_winner_id) {
          const p = allTeamPlayers.find((x: any) => x.id === r.point_winner_id);
          const entry = playerStarsMap.get(r.point_winner_id) ?? { playerName: p?.name ?? '', teamShortName: team?.short_name ?? '', throwerStars: 0, acerStars: 0, catcherStars: 0, totalStars: 0 };
          entry.throwerStars += 1; entry.totalStars += 1;
          playerStarsMap.set(r.point_winner_id, entry);
        }
      }
    }
    if (Array.isArray(m.best_catcher_ids)) {
      for (const pid of m.best_catcher_ids) {
        const p = allTeamPlayers.find((x: any) => x.id === pid);
        const team = tournamentTeams.find((t: any) => t.players?.some((x: any) => x.id === pid));
        const entry = playerStarsMap.get(pid) ?? { playerName: p?.name ?? '', teamShortName: team?.short_name ?? '', throwerStars: 0, acerStars: 0, catcherStars: 0, totalStars: 0 };
        entry.catcherStars += 1; entry.totalStars += 1;
        playerStarsMap.set(pid, entry);
      }
    }
  }
  const playerStars = Array.from(playerStarsMap.values()).sort((a, b) => b.totalStars - a.totalStars);
  const playerStarsRows: (string | number)[][] = [
    ['Player', 'Team', 'Thrower Stars', 'Acer Stars', 'Catcher Stars', 'Total Stars'],
    ...playerStars.map((row: any) => [
      row.playerName || 'N/A',
      row.teamShortName || 'N/A',
      row.throwerStars,
      row.acerStars,
      row.catcherStars,
      row.totalStars,
    ]),
  ];

  console.log('[ExportTournamentData] Sheets: matches=', matchesData.length - 1, 'sets=', setScoresData.length - 1, 'pom=', pomRows.length - 1, 'points=', pointsTableRows.length - 1, 'stars=', playerStarsRows.length - 1);

  // ── Participating Teams sheet ─────────────────────────────────────
  const participatingTeamsRows: (string | number)[][] = [
    ['Team Name', 'Short Name', 'Color'],
    ...participatingTeams.map((t: any) => [
      t.name || 'N/A',
      t.short_name || 'N/A',
      t.color || 'N/A',
    ]),
  ];

  // ── All Timeouts sheet ───────────────────────────────────────────
  const allTimeoutsRows: (string | number)[][] = [
    ['Match #', 'Set #', 'Team', 'Start Time', 'End Time', 'Duration (seconds)'],
  ];
  for (const m of matches) {
    if (!m || !m.timeouts) continue;
    for (const t of m.timeouts as any[]) {
      allTimeoutsRows.push([
        safeNum(m.match_number),
        safeNum(t.set_number),
        safeStr(t.team_name),
        safeStr(t.start_timestamp),
        safeStr(t.end_timestamp),
        safeNum(t.duration_seconds),
      ]);
    }
  }

  // ── Build workbook ─────────────────────────────────────────────
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(participatingTeamsRows), 'Participating Teams');
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(matchesData), 'All Matches');
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(setScoresData), 'All Set Scores');
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(pomRows), 'Player Of The Match');
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(pointsTableRows), 'Points Table');
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(playerStarsRows), 'Player Stars');
  if (allTimeoutsRows.length > 1) {
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(allTimeoutsRows), 'All Timeouts');
  }
  XLSX.writeFile(wb, `tournament_export.xlsx`);
  console.log('[ExportTournamentData] Excel written successfully');
}